import type { MascotContext } from "./contexts";

/**
 * Polish copy per kontekst maskotki. Każdy moment ma:
 * - `headline` — duża linia (32px display)
 * - `sub` — mniejszy podtytuł (14px)
 * - `cta` — tekst CTA buttona
 *
 * Wspierają gendered czasowniki przez parametr `f` (female):
 * `f ? "Zrobiłaś" : "Zrobiłeś"`.
 *
 * Dane runtime (rewardMinor, levelTitle, badgeTitle, childName, streakDays)
 * trafiają jako placeholdery `{name}`/`{n}`/`{title}` w stringach — funkcja
 * `momentCopy()` podstawia.
 */

export type MomentData = {
  childName?: string;
  rewardMinor?: number;
  /** Typ chore z którego wynika moment — wpływa na komunikat. */
  choreKind?: "family" | "extra" | "sport";
  /** Aktywność poza ekranem — wpływa na komunikat (priorytet nad kind). */
  offline?: boolean;
  /**
   * Płeć dorosłego — używane w `parent-philosophy` żeby wybrać maskotkę
   * Mama/Tata. Ignorowane w innych kontekstach (Sianko jest neutralny).
   */
  parentGender?: "m" | "f" | null;
  levelTitle?: string;
  level?: number;
  badgeTitle?: string;
  streakDays?: number;
  goalTitle?: string;
};

export type MomentStep =
  | { kind?: "bubble"; bubble: string; cta: string }
  | { kind: "cards"; headline: string; sub?: string; cta: string };

export type MomentCopy = {
  headline: string;
  sub: string;
  cta: string;
  /**
   * Wieloekranowy moment — gdy obecne, MascotMoment renderuje ekran w trybie
   * dymków (każdy step = osobna grafika + bubble + CTA). Pola headline/sub
   * zostają puste i są ignorowane.
   */
  steps?: MomentStep[];
};

const formatPLN = (minor?: number): string =>
  minor == null ? "" : `${(minor / 100).toFixed(2).replace(".", ",")} zł`;

/**
 * Zwraca polskie copy dla momentu. `female` ustawia formę żeńską czasownika.
 */
export function momentCopy(
  context: MascotContext,
  data: MomentData = {},
  female: boolean = false,
): MomentCopy {
  const name = data.childName?.split(" ")[0] || "";
  const reward = formatPLN(data.rewardMinor);

  switch (context) {
    case "task-submitted":
      // Trzy warianty komunikatu w priorytecie:
      //   1. offline (niezależnie od kind) — sygnał "działanie poza ekranem"
      //   2. family — pomoc rodzinie, +XP, brak PLN
      //   3. extra (default) — z kwotą PLN
      if (data.offline) {
        return {
          headline: name ? `Brawo ${name}!` : "Brawo!",
          sub: "Działanie poza ekranem 🌳 Czeka na zatwierdzenie rodzica.",
          cta: "Wracam do działania",
        };
      }
      if (data.choreKind === "family") {
        return {
          headline: name ? `Dzięki ${name}! 💛` : "Dzięki za pomoc! 💛",
          sub: "Pomoc rodzinie. Czeka na zatwierdzenie rodzica.",
          cta: "Lecimy dalej!",
        };
      }
      return {
        headline: name ? `Brawo ${name}!` : "Brawo!",
        sub: reward
          ? `Zarobione ${reward}. Czeka na zatwierdzenie rodzica.`
          : "Czeka na zatwierdzenie rodzica.",
        cta: "Lecimy dalej!",
      };

    case "task-approved":
      if (data.offline) {
        return {
          headline: "Brawo! 🌳",
          sub: "Działanie poza ekranem zatwierdzone. Tak trzymaj!",
          cta: "OK",
        };
      }
      if (data.choreKind === "family") {
        return {
          headline: "Dzięki za pomoc! 💛",
          sub: "Pomoc rodzinie zatwierdzona. To część bycia rodziną.",
          cta: "OK",
        };
      }
      return {
        headline: `${female ? "Zarobiłaś" : "Zarobiłeś"} ${reward || "monety"}!`,
        sub: "Rodzic zatwierdził zadanie. Kontynuuj świetną robotę!",
        cta: "OK",
      };

    case "level-up":
      return {
        headline: `Nowy poziom!`,
        sub: data.levelTitle
          ? `${data.levelTitle}${data.level ? ` · poziom ${data.level}` : ""}`
          : `Poziom ${data.level ?? "?"}`,
        cta: "Pokaż odznaki",
      };

    case "badge-unlocked":
      return {
        headline: data.badgeTitle ?? "Nowa odznaka!",
        sub: female ? "Odblokowałaś nową odznakę dla Sianka!" : "Odblokowałeś nową odznakę dla Sianka!",
        cta: "Pokaż odznakę",
      };

    case "streak-milestone":
      return {
        headline: data.streakDays ? `${data.streakDays} dni z rzędu!` : "Streak rośnie!",
        sub: "Konsekwencja się opłaca. Tak dalej!",
        cta: "Super!",
      };

    case "streak-broken":
      return {
        headline: "Streak się zerwał…",
        sub: "Spokojnie. Nowy dzień, nowa szansa! Zacznij od jednego zadania.",
        cta: "Zaczynam od nowa",
      };

    case "shield-used":
      return {
        headline: "Tarcza uratowała Twój streak!",
        sub: data.streakDays
          ? `Kontynuujesz ${data.streakDays}-dniowy streak. Tarcza odnowi się w poniedziałek.`
          : "Streak żyje. Tarcza odnowi się w poniedziałek.",
        cta: "Dzięki!",
      };

    case "goal-reached":
      return {
        headline: data.goalTitle ? `Cel zdobyty: ${data.goalTitle}!` : "Cel zdobyty!",
        sub: female
          ? "Uzbierałaś całą kwotę. Możesz odebrać wypłatę u rodzica."
          : "Uzbierałeś całą kwotę. Możesz odebrać wypłatę u rodzica.",
        cta: "Świętuj!",
      };

    case "first-payout":
      return {
        headline: "Pierwsza wypłata!",
        sub: female
          ? `Dostałaś ${reward || "kieszonkowe"}. To pierwsza z wielu!`
          : `Dostałeś ${reward || "kieszonkowe"}. To pierwsza z wielu!`,
        cta: "Świętuj!",
      };

    case "empty-state":
      return {
        headline: "Brak zadań na dziś",
        sub: "Sianko zasnęło. Poproś rodzica o nowe zadanie albo zaproponuj swoje!",
        cta: "Zaproponuj zadanie",
      };

    case "comeback":
      return {
        headline: name ? `Witaj z powrotem, ${name}!` : "Witaj z powrotem!",
        sub: "Sianko za Tobą tęskniło. Zaczynamy od jednego zadania?",
        cta: "Zaczynam!",
      };

    case "welcome":
      return {
        headline: name ? `Cześć ${name}!` : "Witaj w Sianko!",
        sub: "Tu zarabiasz na swoje cele. Zacznij od pierwszego zadania!",
        cta: "Zaczynam!",
      };

    case "complete-day":
      return {
        headline: "Komplet dnia!",
        sub: female
          ? "Zrobiłaś wszystkie dzisiejsze zadania. Pełen wynik!"
          : "Zrobiłeś wszystkie dzisiejsze zadania. Pełen wynik!",
        cta: "Lecimy dalej!",
      };

    case "parent-philosophy":
      return {
        headline: "",
        sub: "",
        cta: "",
        steps: [
          {
            bubble: "Misja aplikacji: mniej scrollowania, więcej działania.",
            cta: "Dalej",
          },
          {
            bubble:
              "W rodzinie nie ma pensji za bycie razem. Trzy typy zadań, " +
              "jedna zasada — zaraz pokażę.",
            cta: "Pokaż przykłady",
          },
          {
            kind: "cards",
            headline: "Trzy typy zadań",
            sub: "Dlatego w Sianko nie wpisujesz wszystkiego — i nie wszystko jest płatne.",
            cta: "Rozumiem, zaczynamy",
          },
        ],
      };
  }
}
